import {
  Close,
  Info,
  ContentCopyOutlined,
  CancelRounded,
} from "@mui/icons-material";
import { Button, Dialog, TextField, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../../axios/axios";
import vpnIcon from "../../../assets/baseVersion/Capa_1.svg";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import "./AddVpn.scss";
import { toast } from "react-toastify";
import React from "react";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";

const AddVpn = ({ open, onClose, OnAddVpnCall }: any) => {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [addVpndata, setAddVpndata] = useState<any>([]);
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);

  const lastErrorMessageRef = React.useRef<string | null>(null);
  const toastTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const toastDuration = 5000;

  const formik = useFormik({
    initialValues: {
      tunnelName: "",
      tunnelDescription: "",
      customerGwIp: "",
      customerCidrBlock: "",
    },
    validationSchema: Yup.object({
      tunnelName: Yup.string()
        .min(3, "Minimum 3 characters")
        .max(20, "Maximum 20 characters")
        .required("Tunnel name is required"),
      tunnelDescription: Yup.string()
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters")
        .required("Tunnel description is required"),
      customerGwIp: Yup.string()
        .min(3, "Minimum 3 characters")
        .max(20, "Maximum 20 characters")
        .required("Customer Gateway IP is required"),
      customerCidrBlock: Yup.string()
        .min(3, "Minimum 3 characters")
        .max(20, "Maximum 20 characters")
        .required("Customer CIDR Block is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        customer_cidr: values.customerCidrBlock,
        customer_gw_ip: values.customerGwIp,
        customer_private_ip: values.customerGwIp, // Add this field if needed
        description: values.tunnelDescription,
        name: values.tunnelName,
      };
      setLoading(true);
      try {
        const response = await axiosInstance.post("/securityscan/vpn", payload);

        if (response.status === 200) {
          setAddVpndata(response?.data?.data);
          toast(response.data.message, { type: "success" });
          setIsSuccess(true); // Show success message
          OnAddVpnCall();
        } else {
          const errorMessage =
            response.data && response.data.message
              ? response.data.message
              : "Failed to create tunnel";
          if (
            lastErrorMessageRef.current !== errorMessage ||
            !toastTimeoutRef.current
          ) {
            setIsButtonDisabled(true);
            toast(errorMessage, { type: "error" });
            lastErrorMessageRef.current = errorMessage;
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current);
            }
            toastTimeoutRef.current = setTimeout(() => {
              toastTimeoutRef.current = null;
              lastErrorMessageRef.current = null;
              setIsButtonDisabled(false);
            }, toastDuration);
          }
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Something went wrong";
        if (
          lastErrorMessageRef.current !== errorMessage ||
          !toastTimeoutRef.current
        ) {
          setIsButtonDisabled(true);
          toast(errorMessage, { type: "error" });
          lastErrorMessageRef.current = errorMessage;
          if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
          }
          toastTimeoutRef.current = setTimeout(() => {
            toastTimeoutRef.current = null;
            lastErrorMessageRef.current = null;
            setIsButtonDisabled(false);
          }, toastDuration);
        }
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  const resetForm = () => {
    formik.resetForm();
    setIsSuccess(false);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log(`Copied: ${text}`);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onClose();
          resetForm();
        }}
        sx={{
          "& .MuiPaper-root": {
            width: "350px",
            padding: "24px",
          },
        }}
      >
        <div className="vpn-modalbox">
          {!isSuccess ? (
            <form onSubmit={formik.handleSubmit}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    paddingBottom: "5px",
                    fontWeight: "700",
                    margin: "0",
                  }}
                >
                  Add VPN
                </h3>

                <CancelRoundedIcon
                  sx={{ color: "#A6A5B6", cursor: "pointer" }}
                  onClick={onClose}
                />
              </div>
              <div>
                <TextField
                  label="Tunnel name"
                  fullWidth
                  variant="outlined"
                  className="mb_3"
                  name="tunnelName"
                  value={formik.values.tunnelName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.tunnelName &&
                    Boolean(formik.errors.tunnelName)
                  }
                  helperText={
                    formik.touched.tunnelName && formik.errors.tunnelName
                  }
                />
                <TextField
                  label="Tunnel description"
                  fullWidth
                  variant="outlined"
                  className="mb_3"
                  name="tunnelDescription"
                  value={formik.values.tunnelDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.tunnelDescription &&
                    Boolean(formik.errors.tunnelDescription)
                  }
                  helperText={
                    formik.touched.tunnelDescription &&
                    formik.errors.tunnelDescription
                  }
                />
                <TextField
                  label="Customer Gateway IP"
                  fullWidth
                  variant="outlined"
                  className="mb_3"
                  name="customerGwIp"
                  value={formik.values.customerGwIp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.customerGwIp &&
                    Boolean(formik.errors.customerGwIp)
                  }
                  helperText={
                    formik.touched.customerGwIp && formik.errors.customerGwIp
                  }
                />
                <TextField
                  label="Customer CIDR Block"
                  fullWidth
                  variant="outlined"
                  name="customerCidrBlock"
                  value={formik.values.customerCidrBlock}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.customerCidrBlock &&
                    Boolean(formik.errors.customerCidrBlock)
                  }
                  helperText={
                    formik.touched.customerCidrBlock &&
                    formik.errors.customerCidrBlock
                  }
                />
                <p className="info-text">
                  <Info />
                  Add multiple CIDR block using comma (,).
                </p>
              </div>
              <Button
                type="submit"
                sx={{
                  background: "#3a2ada",
                  width: "160px",
                  marginTop: "20px",
                  color: "white",
                  textTransform: "none",
                  height: "30px",
                  fontSize: "12px",
                  "&:focus": { outline: "none" },
                }}
                disabled={
                  !formik.isValid ||
                  formik.isSubmitting ||
                  isButtonDisabled ||
                  loading ||
                  !formik.dirty
                }
                endIcon={
                  loading ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : null
                }
              >
                Create Tunnel
              </Button>
            </form>
          ) : (
            <div style={{ width: "300px", padding: "20px" }}>
              <img src={vpnIcon} alt="VPN Icon" />
              <h5
                style={{
                  paddingTop: "0px",
                  marginTop: "0px",
                  fontSize: "16px",
                  fontWeight: "700",
                }}
              >
                Tunnel Successfully Created
              </h5>
              <div>
                <div className="vpn-details">
                  <div className="tunnel-details">
                    <p>SPARK VPN Public IP</p>
                    <p>{addVpndata.spark_vpn_public_id}</p>
                  </div>
                  <div
                    className="copy-icon"
                    onClick={() => handleCopy(addVpndata.spark_vpn_public_id)}
                  >
                    <ContentCopyOutlined className="copyIcon-size" />
                  </div>
                </div>
                <div className="vpn-details">
                  <div className="tunnel-details">
                    <p>SPARK Subnet</p>
                    <p>{addVpndata.spark_subnet}</p>
                  </div>
                  <div
                    className="copy-icon"
                    onClick={() => handleCopy(addVpndata.spark_subnet)}
                  >
                    <ContentCopyOutlined className="copyIcon-size" />
                  </div>
                </div>
                <div className="vpn-details">
                  <div className="tunnel-details">
                    <p>IKE Key</p>
                    <p>{showOtp ? addVpndata.ike_key : "********"}</p>
                    {/* <p>{addVpndata.ike_key}</p> */}
                  </div>
                  <div
                    className="copy-seen"
                    onClick={() => handleCopy(addVpndata.ike_key)}
                  >
                    <button
                      onClick={() => setShowOtp(!showOtp)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {!showOtp && (
                        <VisibilityIcon
                          sx={{ width: "16px", height: "16px" }}
                        />
                      )}
                      {showOtp && (
                        <VisibilityOffIcon
                          sx={{ width: "16px", height: "16px" }}
                        />
                      )}
                    </button>
                    <ContentCopyOutlined className="copyIcon-size" />
                  </div>
                </div>
                {/* <div>
                                    <p>{addVpndata?.tunnel_name}</p>
                                    <p>{addVpndata?.tunnel_description}</p>
                                </div> */}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                  marginTop: "5px",
                }}
              >
                <Button
                  sx={{
                    background: "#3a2ada",
                    color: "white",
                    width: "140px",
                    textTransform: "none",
                    height: "30px",
                    fontSize: "12px",
                    "&:focus": { outline: "none" },
                  }}
                  onClick={resetForm}
                >
                  Add Another VPN
                </Button>
                <Button
                  sx={{
                    border: "1px solid #3a2ada",
                    height: "30px",
                    width: "120px",
                    fontSize: "12px",
                    textTransform: "none",

                    "&:focus": { outline: "none" },
                  }}
                  onClick={() => {
                    onClose();
                    resetForm();
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default AddVpn;
